<template>
  <div class="container mt-4">

    <!-- Top Bar -->
    <div class="d-flex justify-content-between align-items-center mb-3 p-1">
      <div>
        <h2 class="mb-0 main-title">Import Assets</h2>
        <p class="mb-0 fs-12 text-muted">Bulk create assets from a spreadsheet file</p>
      </div>
    </div>
    <div class="instructions-card mb-3 position-relative">
      <div class="download-btn-wrapper">
        <a href="/templates/asset_template.xlsx"
           download="asset_template.xlsx"
           class="btn btn-download">
          <i class="fa fa-download me-2"></i>
          <span>Download Template</span>
        </a>
      </div>

      <div class="instructions-header">
        <i class="fa fa-lightbulb-o instructions-icon"></i>
        <span class="instructions-title">Quick Start Guide</span>
      </div>

      <div class="steps-row">
        <div class="step-item">
          <div class="step-number">1</div>
          <div class="step-icon-wrap icon-blue"><i class="fa fa-download"></i></div>
          <div class="step-text">
            <div class="step-title">Download template</div>
            <div class="step-desc">Get the spreadsheet with correct columns</div>
          </div>
        </div>

        <div class="step-divider"><i class="fa fa-chevron-right"></i></div>

        <div class="step-item">
          <div class="step-number">2</div>
          <div class="step-icon-wrap icon-orange"><i class="fa fa-pencil-square"></i></div>
          <div class="step-text">
            <div class="step-title">Fill in data</div>
            <div class="step-desc"><span class="highlight-red">Red fields</span> are mandatory</div>
          </div>
        </div>

        <div class="step-divider"><i class="fa fa-chevron-right"></i></div>

        <div class="step-item">
          <div class="step-number">3</div>
          <div class="step-icon-wrap icon-green"><i class="fa fa-upload"></i></div>
          <div class="step-text">
            <div class="step-title">Upload file</div>
            <div class="step-desc">Save as CSV / XLSX and upload below</div>
          </div>
        </div>
      </div>
    </div>

<div class="col-ref-alert mt-3 mb-3">
  <i class="fa fa-info-circle me-2" style="color: #0d6efd;"></i>
  <span class="alert-text">
    <strong>Column reference:</strong> 
    <span class="text-danger">Required</span> — name, code, serial_number, model, manufacturer, purchase_date, criticality, category
    &nbsp;&nbsp;|&nbsp;&nbsp; 
    <span class="text-muted">Optional</span> — description, location_ids, vendor_ids, material_ids
  </span>
</div>

    <!-- Upload Card -->
    <div class="card p-3 bg-white mb-3">
      <div class="upload-header">
        <i class="fa fa-cloud-upload upload-icon"></i>
        <span class="section-label">Upload file</span>
      </div>

      <div class="drop-zone w-100" :class="{ 'drag-over': isDragging, 'has-file': selectedFile }"
        @dragover.prevent="isDragging = true" @dragleave="isDragging = false" @drop.prevent="onDrop">
        <input type="file" class="dz-input" accept=".xlsx,.xls,.csv" ref="fileInput" @change="onFileChange" />
        <i class="fa dz-icon" :class="selectedFile ? 'fa-file-excel-o' : 'fa-cloud-upload'"></i>
        <div class="dz-title">
          {{ selectedFile ? 'File ready — click "Import assets" to proceed'
            : 'Drag & drop your file here, or click to browse' }}
        </div>
        <div class="dz-hint" v-if="!selectedFile">
          <i class="fa fa-info-circle"></i> Accepted: <code>.xlsx</code> <code>.xls</code> <code>.csv</code>
          &nbsp;·&nbsp; Comma-separate multiple: <code>vendor_ids</code> <code>material_ids</code> <code>location_ids</code>
        </div>
        <div v-if="selectedFile" class="dz-file-row">
          <i class="fa fa-file-excel-o"></i>
          <span class="dz-file-name">{{ selectedFile.name }}</span>
          <span class="dz-file-size">{{ fileSizeLabel }}</span>
        </div>
      </div>

      <div class="d-flex align-items-center gap-2 mt-3">
        <button class="btn btn-primary btn-sm fs-12 fw-normal text-white"
          @click="submitImport" :disabled="!selectedFile || importing">
          <span v-if="importing">
            <span class="spinner-border spinner-border-sm me-1" role="status"></span> Importing...
          </span>
          <span v-else>
            <i class="fa fa-upload me-1"></i> Import assets
          </span>
        </button>
        <button v-if="selectedFile" class="btn btn-outline-danger btn-sm fs-12" @click="clearFile">
          <i class="fa fa-trash-o me-1"></i> Clear
        </button>
      </div>
    </div>

    <!-- Stats Row -->
    <div v-if="allMessages.length > 0" class="stats-row mb-3">
      <div class="stat-card">
        <div class="stat-icon icon-blue"><i class="fa fa-list-ul"></i></div>
        <div>
          <div class="stat-label">Total rows</div>
          <div class="stat-val blue">{{ allMessages.length }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon icon-green"><i class="fa fa-check-circle"></i></div>
        <div>
          <div class="stat-label">Successful</div>
          <div class="stat-val green">{{ successCount }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon icon-red"><i class="fa fa-exclamation-circle"></i></div>
        <div>
          <div class="stat-label">Failed</div>
          <div class="stat-val red">{{ failCount }}</div>
        </div>
      </div>
    </div>

    <!-- Results Table -->
    <div class="card p-3 bg-white" v-if="allMessages.length > 0">
      <div class="tab-group mb-3">
        <button v-for="t in tabs" :key="t.value"
          class="tab-btn" :class="{ 'tab-active': activeTab === t.value }"
          @click="activeTab = t.value">
          <span class="tab-dot" :class="'dot-' + t.value"></span>
          {{ t.label }}
          <span class="tab-count" :class="'count-' + t.value">{{ tabCount(t.value) }}</span>
        </button>
      </div>

      <LoadingBar v-if="importing" message="Processing import..." />

      <div v-else class="table-wrapper">
        <ListTable>
          <colgroup>
            <col style="width: 5%">
            <col style="width: 10%">
            <col style="width: 25%">
            <col style="width: 15%">
            <col style="width: 45%">
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Status</th>
              <th>Name</th>
              <th>Code</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="msg in filteredMessages" :key="msg.row_counter"
              :class="msg.status === 'success' ? 'table-success' : 'table-danger'">
              <td class="fs-11 text-muted">{{ msg.row_counter }}</td>
              <td>
                <span class="result-pill" :class="msg.status === 'success' ? 'pill-s' : 'pill-f'">
                  <i :class="msg.status === 'success' ? 'fa fa-check' : 'fa fa-times'"></i>
                  {{ msg.status === 'success' ? 'Success' : 'Failed' }}
                </span>
              </td>
              <td class="fs-12">{{ msg.name ?? 'N/A' }}</td>
              <td class="fs-12">{{ msg.code ?? 'N/A' }}</td>
              <td class="fs-12" :class="msg.status === 'success' ? 'text-success' : 'text-danger'">
                {{ msg.message }}
              </td>
            </tr>
            <tr v-if="filteredMessages.length === 0">
              <td colspan="5" class="text-center fs-12 text-muted py-4">No records to show</td>
            </tr>
          </tbody>
        </ListTable>
      </div>
    </div>

  </div>
</template>
<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import ListTable from '@/components/utils/ListTable.vue'

export default {
  name: 'ImportAssets',
  components: { LoadingBar, ListTable },
  data() {
    return {
      selectedFile: null,
      importing: false,
      isDragging: false,
      allMessages: [],
      activeTab: 'all',
      tabs: [
        { label: 'All',     value: 'all' },
        { label: 'Success', value: 'success' },
        { label: 'Failed',  value: 'fail' },
      ],

    }
  },
  computed: {
    successCount() {
      return this.allMessages.filter(m => m.status === 'success').length
    },
    failCount() {
      return this.allMessages.filter(m => m.status === 'fail').length
    },
    filteredMessages() {
      if (this.activeTab === 'all') return this.allMessages
      return this.allMessages.filter(m => m.status === this.activeTab)
    },
    fileSizeLabel() {
      if (!this.selectedFile) return ''
      const kb = this.selectedFile.size / 1024
      return kb > 1024
        ? (kb / 1024).toFixed(2) + ' MB'
        : kb.toFixed(1) + ' KB'
    },
  },
  methods: {
    tabCount(value) {
      if (value === 'all')     return this.allMessages.length
      if (value === 'success') return this.successCount
      if (value === 'fail')    return this.failCount
      return 0
    },
    onFileChange(e) {
      this.selectedFile = e.target.files[0] || null
      this.allMessages  = []
    },
    onDrop(e) {
      this.isDragging  = false
      const f = e.dataTransfer.files[0]
      if (f) { this.selectedFile = f; this.allMessages = [] }
    },
    clearFile() {
      this.selectedFile = null
      this.$refs.fileInput.value = ''
      this.allMessages = []
    },
    async submitImport() {
      if (!this.selectedFile) return
      this.importing   = true
      this.allMessages = []

      const formData = new FormData()
      formData.append('file', this.selectedFile)

      const result = await form_api_submit({
        url: 'asset/import',
        method: 'POST',
        data: formData,
        show_success_toast: true,
        show_error_toast: true,
        remove_pre_toasts: false,
      })

      if (result.success) {
        this.allMessages = result.data.all_messages ?? []
      }

      this.importing = false
    },
  },
}
</script>
